import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DownloadIcon from "@mui/icons-material/Download";
import "./topbar.css";
import { useRef } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { useSnackbar } from "notistack";
import { Badge, List, ListItem, ListItemText, Divider } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

export default function Topbar() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateResignedError, setDateResignedError] = useState(false);

  const role = localStorage.getItem("roleAccount");

  const allowedRoles = [
    "HR HEAD",
    "HR SPECIALIST",
    "HR COMPENSATION AND BENEFITS",
    "HR COORDINATOR SPECIALIST",
    "MIS",
  ];

  const { enqueueSnackbar } = useSnackbar();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [bellAnchor, setBellAnchor] = useState(null);
  const [faqAnchor, setFaqAnchor] = useState(null);
  const socketRef = useRef(null);

  const canEdit = allowedRoles.includes(role);
  const [anchorEl, setAnchorEl] = useState(null);

  const fullName = localStorage.getItem("adminFullName");
  const roleAccount = localStorage.getItem("roleAccount");

  // Listen to sidebar state from localStorage
  useEffect(() => {
    const checkSidebarState = () => {
      const isOpen = localStorage.getItem("sidebarOpen") === "true";
      setSidebarOpen(isOpen);
    };

    checkSidebarState();

    // Listen for storage changes
    window.addEventListener("storage", checkSidebarState);

    // Poll for changes (since localStorage doesn't trigger storage event in same window)
    const interval = setInterval(checkSidebarState, 100);

    return () => {
      window.removeEventListener("storage", checkSidebarState);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!role) return;
    axios
      .get("https://api-map.bmphrc.com/notifications", { params: { role } })
      .then((r) => {
        const items = r.data.data || [];
        setNotifs(items);
        setUnread(
          items.filter((n) => !(n.readBy || []).includes(fullName)).length,
        );
      })
      .catch(() => {});
  }, [role, fullName]);

  // live channel
  useEffect(() => {
    if (!role) return;
    const socket = io("https://api-map.bmphrc.com", {
      auth: { role, fullName },
    });
    socketRef.current = socket;
    socket.on("notification", (n) => {
      console.log("🔔 notification received:", n);
      if (n.updatedBy === fullName) return; // don't ping myself for my own action
      setNotifs((prev) => [n, ...prev]);
      setUnread((u) => u + 1);
      enqueueSnackbar(n.message, { variant: "info", autoHideDuration: 5000 });
    });
    return () => socket.disconnect();
  }, [role, fullName, enqueueSnackbar]);

  const openBell = (e) => {
    setBellAnchor(e.currentTarget);
    if (unread > 0) {
      axios
        .post("https://api-map.bmphrc.com/notifications/mark-read", {
          user: fullName,
          role,
        })
        .catch(() => {});
      setUnread(0);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace("/");
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "#0f2a44",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        zIndex: 1200,
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar
        sx={{
          minHeight: "64px !important",
          px: 3,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* Left Section - Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box className="sidebar-logo">
            <img
              src="/nexus-logo-icon-navy.svg"
              alt="Nexus Desk"
              style={{ width: 22, height: 22 }}
            />
          </Box>
          <Box
            sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}
          >
            <Typography
              component="div"
              sx={{
                fontWeight: 700,
                color: "white",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                letterSpacing: "0.5px",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              NEXUS DESK
            </Typography>
            <Typography
              component="div"
              sx={{
                color: "rgba(144, 224, 239, 0.75)",
                fontSize: "10px",
                letterSpacing: "1.5px",
                display: { xs: "none", sm: "block" },
              }}
            >
              HRIS
            </Typography>
          </Box>
        </Box>

        {/* Right Section - Alerts + Account */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={openBell}
            sx={{
              color: "white",
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.06)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
            }}
          >
            <Badge
              badgeContent={unread}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  border: "2px solid #0f2a44",
                  fontWeight: 700,
                  fontSize: "9px",
                },
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* FAQ / Help - User Manual */}
          <IconButton
            onClick={(e) => setFaqAnchor(e.currentTarget)}
            title="Help & User Manual"
            sx={{
              color: "white",
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.06)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
            }}
          >
            <HelpOutlineIcon />
          </IconButton>

          <Box
            sx={{
              width: "1px",
              height: 30,
              backgroundColor: "rgba(255,255,255,0.12)",
              mx: 0.5,
            }}
          />

          {/* Account pill (name + avatar) — opens the dropdown */}
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              pl: { xs: 0.5, md: 1.5 },
              pr: 0.5,
              py: 0.5,
              borderRadius: "12px",
              cursor: "pointer",
              transition: "background 0.2s ease",
              backgroundColor: "rgba(255,255,255,0.05)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
            }}
          >
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                alignItems: "flex-end",
                lineHeight: 1.25,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: "13.5px",
                  lineHeight: 1.2,
                }}
              >
                {fullName || "Admin User"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "11px",
                }}
              >
                {roleAccount || "Administrator"}
              </Typography>
            </Box>

            <Avatar
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                backgroundColor: "#90e0ef",
                color: "#0f2a44",
                fontWeight: 700,
                fontSize: "15px",
              }}
            >
              {fullName ? fullName.charAt(0).toUpperCase() : "A"}
            </Avatar>
          </Box>

          <Menu
            anchorEl={bellAnchor}
            open={Boolean(bellAnchor)}
            onClose={() => setBellAnchor(null)}
            PaperProps={{ sx: { mt: 1.5, width: 340, maxHeight: 420 } }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {notifs.length === 0 && (
              <ListItem sx={{ py: 2, justifyContent: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#999" }}>
                  No notifications yet
                </Typography>
              </ListItem>
            )}
            {notifs.map((n, i) => (
              <Box key={n._id || i}>
                <ListItem sx={{ py: 1, display: "block" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {n.message}
                  </Typography>

                  {(n.changes || [])
                    .filter(
                      (c) => c.field !== "Outlet" && c.field !== "updatedBy",
                    )
                    .map((c, j) => (
                      <Typography
                        key={j}
                        sx={{ fontSize: 12, color: "#555", mt: 0.3 }}
                      >
                        {n.activityType === "NEW_EMPLOYEE"
                          ? `${c.field}: ${c.newValue}`
                          : `${c.field}: ${c.oldValue} → ${c.newValue}`}
                      </Typography>
                    ))}

                  <Typography sx={{ fontSize: 11, color: "#999", mt: 0.5 }}>
                    {new Date(n.date).toLocaleString("en-PH")}
                  </Typography>
                </ListItem>
                {i < notifs.length - 1 && <Divider />}
              </Box>
            ))}
          </Menu>

          {/* FAQ / User Manual Menu */}
          <Menu
            anchorEl={faqAnchor}
            open={Boolean(faqAnchor)}
            onClose={() => setFaqAnchor(null)}
            PaperProps={{ sx: { mt: 1.5, minWidth: 240, borderRadius: "8px" } }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e0e0e0" }}>
              <Typography
                sx={{ fontWeight: 700, fontSize: 14, color: "#0f2a44" }}
              >
                Help & Support
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#666" }}>
                Nexus Desk User Manual
              </Typography>
            </Box>
            <MenuItem
              component="a"
              href="/Nexus-Desk-User-Manual.pdf"
              download
              onClick={() => setFaqAnchor(null)}
              sx={{
                py: 1.5,
                color: "#0f2a44",
                "&:hover": { backgroundColor: "rgba(15, 42, 68, 0.06)" },
              }}
            >
              <DownloadIcon sx={{ mr: 1.5, fontSize: 20 }} />
              <Typography variant="body2">Download User Manual</Typography>
            </MenuItem>
          </Menu>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                borderRadius: "8px",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {/* Mobile: Show user info in dropdown */}
            <Box
              sx={{
                display: { xs: "block", md: "none" },
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#0f2a44",
                  fontSize: "14px",
                }}
              >
                {fullName || "Admin User"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: "12px",
                }}
              >
                {roleAccount || "Administrator"}
              </Typography>
            </Box>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleLogout();
              }}
              sx={{
                py: 1.5,
                color: "#d32f2f",
                "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                },
              }}
            >
              <LogoutIcon sx={{ mr: 1.5 }} />
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
